import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  CreditCard, 
  Lock, 
  ArrowLeft,
  CheckCircle,
  Loader2
} from 'lucide-react'

interface CartItem {
  id: string
  title: string
  price: number
  originalPrice?: number
}

declare global {
  interface Window {
    Razorpay: any
  }
}

export default function Checkout() {
  const navigate = useNavigate()
  const [cart, setCart] = useState<CartItem[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [user, setUser] = useState<any>(null)

  useEffect(() => {
    loadCheckoutData()
  }, [])

  const loadCheckoutData = () => {
    try {
      // Check authentication
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        return
      }

      // Load user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}')
      setUser(userData)

      // Check if this is a single course purchase (Buy Now)
      const urlParams = new URLSearchParams(window.location.search)
      const isSinglePurchase = urlParams.get('type') === 'single'
      
      let cartData: CartItem[] = []
      
      if (isSinglePurchase) {
        // Load from temporary checkout cart (Buy Now flow)
        const tempCart = JSON.parse(localStorage.getItem('tempCheckoutCart') || '[]')
        if (tempCart.length === 0) {
          navigate('/user/courses')
          return
        }
        cartData = tempCart
      } else {
        // Load from regular cart
        const regularCart = JSON.parse(localStorage.getItem('userCart') || '[]')
        if (regularCart.length === 0) {
          navigate('/user/cart')
          return
        }
        cartData = regularCart
      }
      
      setCart(cartData)
    } catch (error) {
      console.error('Error loading checkout data:', error)
      navigate('/user/cart')
    } finally {
      setLoading(false)
    }
  }

  const calculateTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.price, 0)
    return subtotal
  }

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve(true)
      script.onerror = () => resolve(false)
      document.body.appendChild(script)
    })
  }

  const handleCompleteOrder = async () => {
    setProcessing(true)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        return
      }

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript()
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.')
        setProcessing(false)
        return
      }

      const total = calculateTotal()

      // Create order - check if single purchase or cart purchase
      const urlParams = new URLSearchParams(window.location.search)
      const isSinglePurchase = urlParams.get('type') === 'single'
      
      const endpoint = isSinglePurchase ? '/api/payments/create-single-order' : '/api/payments/create-order'
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          courseIds: cart.map(item => item.id),
          amount: total
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create order')
      }

      const orderData = await response.json()

      // Razorpay options
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_dummy',
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: "Sudha's Makeup Academy",
        description: `Enrollment for ${cart.length} course${cart.length > 1 ? 's' : ''}`,
        order_id: orderData.orderId,
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: {
          color: '#9333EA', // purple-600
        },
        handler: async (response: any) => {
          await handlePaymentSuccess(response, orderData.paymentId)
        },
        modal: {
          ondismiss: () => {
            setProcessing(false)
          },
        },
      }

      const razorpay = new window.Razorpay(options)
      razorpay.on('payment.failed', (response: any) => {
        console.error('Payment failed:', response.error)
        alert(`Payment failed: ${response.error.description}`)
        setProcessing(false)
      })
      
      razorpay.open()
    } catch (error) {
      console.error('Checkout error:', error)
      alert('An error occurred. Please try again.')
      setProcessing(false)
    }
  }

  const handlePaymentSuccess = async (razorpayResponse: any, paymentId: string) => {
    const token = localStorage.getItem('token')
    if (!token) return

    try {
      const total = calculateTotal()
      const courseIds = cart.map(item => item.id)

      const response = await fetch('/api/payments/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentId,
          razorpayPaymentId: razorpayResponse.razorpay_payment_id,
          razorpayOrderId: razorpayResponse.razorpay_order_id,
          razorpaySignature: razorpayResponse.razorpay_signature,
          courseIds,
          amount: total
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Store checkout data for the success page
        localStorage.setItem('checkoutData', JSON.stringify({
          items: cart,
          total,
          paymentId: data.paymentId,
          enrollments: data.enrollments
        }))

        // Clear appropriate cart based on purchase type
        const urlParams = new URLSearchParams(window.location.search)
        const isSinglePurchase = urlParams.get('type') === 'single'
        
        if (isSinglePurchase) {
          localStorage.removeItem('tempCheckoutCart')
        } else {
          localStorage.removeItem('userCart')
        }
        
        // Redirect to success page with enrollment confirmation
        navigate(`/payment-success?payment_id=${data.paymentId}&razorpay_payment_id=${razorpayResponse.razorpay_payment_id}&enrolled=${data.enrollments}&amount=${total}`)
      } else {
        alert(data.error || 'Payment verification failed')
      }
    } catch (error) {
      console.error('Payment verification error:', error)
      alert('Payment verification failed. Please contact support.')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-pink-200 dark:border-slate-800 rounded-full animate-spin border-t-pink-600 dark:border-t-pink-400"></div>
      </div>
    )
  }

  const total = calculateTotal()

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => {
            const urlParams = new URLSearchParams(window.location.search)
            const isSinglePurchase = urlParams.get('type') === 'single'
            navigate(isSinglePurchase ? '/user/courses' : '/user/cart')
          }}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Checkout</h1>
          <p className="text-slate-600 dark:text-slate-300">Complete your purchase</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Courses */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="font-semibold text-slate-900 dark:text-white">
                Order Summary ({cart.length} {cart.length === 1 ? 'item' : 'items'})
              </h2>
            </div>
            
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {cart.map((item) => (
                <div key={item.id} className="p-4 flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-900 dark:text-white mb-1">
                      {item.title}
                    </h3>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      ₹{item.price.toLocaleString()}
                    </div>
                    {item.originalPrice && item.originalPrice > item.price && (
                      <div className="text-sm text-slate-500 dark:text-slate-400 line-through">
                        ₹{item.originalPrice.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-blue-50 dark:bg-slate-700/50 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-1">
                  Secure Payment
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Your payment information is encrypted and secure. We use Razorpay, India's leading payment gateway.
                </p>
              </div>
            </div>
          </div>

          {/* Money Back Guarantee */}
          <div className="bg-green-50 dark:bg-slate-700/50 border border-green-200 dark:border-green-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900 dark:text-green-200 mb-1">
                  30-Day Money-Back Guarantee
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Not satisfied? Get a full refund within 30 days, no questions asked.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 sticky top-6">
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4">
              Payment Details
            </h2>

            {/* Total */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                <span>Subtotal:</span>
                <span className="font-medium">₹{total.toLocaleString()}</span>
              </div>
              
              <div className="flex items-center justify-between text-2xl font-bold text-slate-900 dark:text-white pt-3 border-t border-slate-200 dark:border-slate-700">
                <span>Total:</span>
                <span>₹{total.toLocaleString()}</span>
              </div>
            </div>

            {/* Complete Order Button */}
            <button
              onClick={handleCompleteOrder}
              disabled={processing}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white px-6 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 mb-4"
            >
              {processing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  Complete Order
                </>
              )}
            </button>

            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              By completing your purchase you agree to these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}